import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';
import { Form, LocationAutocompleteInput } from '../../../../components';
import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={classNames(css.commonInput, isMobile ? css.mobileInput : css.desktopInput)}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'TopbarSearchForm.placeholder',
              })}
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={classNames(
              css.commonInput,
              isMobile ? css.mobileInput : css.desktopInput
            )}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

const TopbarSearchForm = props => {
  const searchInputRef = useRef(null);
  const intl = useIntl();
  const { appConfig, onSubmit, ...restOfProps } = props;

  const onChange = location => {
    if (location.selectedPlace) {
      onSubmit({ location });
      searchInputRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    onSubmit({ keywords: values.keywords, searchType: values?.searchType });
    searchInputRef?.current?.blur();
  };

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={onKeywordSubmit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
          values,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;
        const isLocationSearch = values?.searchType === 'provider';

        const preventFormSubmit = e => e.preventDefault();
        const submitFormFn = isLocationSearch ? preventFormSubmit : handleSubmit;

        return (
          <Form className={classes} onSubmit={submitFormFn}>
            <div className={css.searchContainer}>
              <Field name="searchType" component="select" className={css.searchTypeSelector}>
                <option value="service">
                  {intl.formatMessage({ id: 'TopbarSearchForm.searchTypeService' })}
                </option>
                <option value="provider">
                  {intl.formatMessage({ id: 'TopbarSearchForm.searchTypeLocation' })}
                </option>
              </Field>
              {isLocationSearch ? (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInputRef}
                  onLocationChange={onChange}
                />
              ) : (
                <KeywordSearchField
                  keywordSearchWrapperClasses={css.keywordSearchWrapper}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInputRef}
                />
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
